import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Logo from '../Logo';
import ThemeToggle from '../ThemeToggle';
import AnimatedNavItem from './AnimatedNavItem';
import { mainNavigation } from '../../config/navigation';
import { useLocation } from 'react-router-dom';

interface AnimatedMobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const AnimatedMobileDrawer: React.FC<AnimatedMobileDrawerProps> = ({
  isOpen,
  onClose
}) => {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const drawerVariants = {
    hidden: {
      x: '100%',
      opacity: 0,
      scale: 0.95
    },
    visible: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        mass: 0.8,
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    },
    exit: {
      x: '100%',
      opacity: 0,
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 25,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }
  };

  const navContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const footerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: 0.5
      }
    }
  };

  const closeButtonVariants = {
    hidden: { opacity: 0, rotate: -90, scale: 0.5 },
    visible: { 
      opacity: 1, 
      rotate: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.4
      }
    },
    hover: {
      scale: 1.1,
      rotate: 90,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      scale: 0.9,
      rotate: 45
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id="mobile-menu"
          className="fixed top-0 right-0 h-full w-80 max-w-[85vw] lg:hidden z-[9999]"
          variants={drawerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '-4px 0 25px rgba(0, 0, 0, 0.15)',
            paddingTop: 'max(env(safe-area-inset-top), 0px)',
            paddingBottom: 'max(env(safe-area-inset-bottom), 0px)'
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          {/* Animated gradient background */}
          <motion.div
            className="absolute inset-0 opacity-5"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 0.05,
              background: [
                'linear-gradient(45deg, #10b981, #059669)',
                'linear-gradient(45deg, #059669, #047857)',
                'linear-gradient(45deg, #047857, #10b981)'
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />

          {/* Header */}
          <motion.div 
            className="flex items-center justify-between p-4 border-b bg-white/80 backdrop-blur-sm relative z-10"
            style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}
            variants={headerVariants}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 20 }}
            >
              <Logo size="sm" hideText={false} />
            </motion.div>
            
            <motion.button
              onClick={onClose}
              className="p-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[48px] min-w-[48px] relative"
              style={{ color: 'var(--color-text-primary)' }}
              aria-label="Close menu"
              variants={closeButtonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {/* Glow effect on hover */}
              <motion.div
                className="absolute inset-0 rounded-lg bg-red-500/10"
                initial={{ scale: 0, opacity: 0 }}
                whileHover={{ scale: 1.2, opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
              <X className="w-6 h-6 relative z-10" />
            </motion.button>
          </motion.div>

          {/* Navigation Items */}
          <motion.div 
            className="flex-1 overflow-y-auto py-4 relative z-10"
            variants={navContainerVariants}
          >
            <nav className="space-y-2 px-6" role="navigation">
              {mainNavigation.map((item, index) => (
                <AnimatedNavItem
                  key={item.path}
                  to={item.path}
                  isActive={isActive(item.path)}
                  onClick={onClose}
                  variant="mobile"
                  index={index}
                >
                  {item.label}
                </AnimatedNavItem>
              ))}
            </nav>
          </motion.div>

          {/* Footer */}
          <motion.div 
            className="p-4 border-t space-y-3 bg-white/80 backdrop-blur-sm relative z-10"
            style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}
            variants={footerVariants}
          >
            {/* Theme Toggle */}
            <motion.div 
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-sm font-medium text-gray-700">
                Theme
              </span>
              <ThemeToggle />
            </motion.div>

            {/* Decorative element */}
            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, type: "spring", stiffness: 200, damping: 20 }}
            >
              <div className="w-12 h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full" />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedMobileDrawer;
